<template>
  <div>
    <transition name="fade">
      <div
        v-if="hasActive"
        class="o-panel--overlay overflow-auto inset-0 w-screen h-screen fixed bg-black opacity-50"
        @click="closeTop()"
      />
    </transition>

    <!-- Dialog targets live outside the TransitionGroup so they exist before
         any panel component mounts. -->
    <div aria-hidden="true" class="contents">
      <div v-for="p in activePanels" :key="`portal-${p.id}`" :id="p.portal" />
    </div>

    <transition-group name="slide-panel" tag="div">
      <div
        v-for="(panel, index) in activePanels"
        :key="panel.id"
        :class="[
          'o-panel overflow-hidden left-0 top-0 bottom-0 right-0 w-screen h-full fixed flex items-center pointer-events-none',
          isModeModal ? 'model-panel' : 'right-panel'
        ]"
      >
        <div :class="containerClass(index, panel)" :style="containerStyle(index)">
          <div
            v-if="order(index)"
            class="h-full w-full absolute inset-0"
            @click="focus(index)"
          />
          <div
            class="flex-grow bg-white dark:bg-gray-900 rounded-t-xl sm:rounded-xl shadow-lg overflow-hidden relative h-full dark:border dark:border-gray-800"
            :class="order(index) ? 'pointer-events-none' : 'pointer-events-auto'"
          >
            <PanelSlot :panel="panel" :focused="!order(index)" />
          </div>
          <!-- Extension content (such as the email preview) must remain a flex
               sibling of the panel body. Moving this target outside the panel
               detaches the preview from its panel and prevents the host from
               resizing to make room for it. -->
          <div
            :id="panel.extension"
            :ref="(target) => observeExtension(panel, target)"
            class="contents"
          />
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, provide, ref, watch } from 'vue'
import { useAuthStore } from '~/stores/useAuthStore'
import { type PanelInstance, usePanelStore } from '~/stores/usePanelStore'
import PanelSlot from './PanelSlot.vue'
import { PanelModeKey } from './panelModeKey'

const store = usePanelStore()
const authStore = useAuthStore()
const route = useRoute()

const activePanels = computed(() => store.activePanels)
const hasActive = computed(() => activePanels.value.length > 0)

// Local mode ref, seeded from the active account's saved preference. PanelSlot
// mutates this directly via the injected key (and persists to authStore for
// next session).
const panelMode = ref<'panel' | 'modal'>('panel')
const extensionActive = ref<Record<string, boolean>>({})
const extensionObservers = new Map<string, MutationObserver>()

function syncModeFromAuth() {
  const acc = (authStore as any).activeAccount
  panelMode.value = acc?.panel === 'modal' ? 'modal' : 'panel'
}

syncModeFromAuth()
watch(() => (authStore as any).activeAccount?.panel, syncModeFromAuth)
provide(PanelModeKey, panelMode)

const isModeModal = computed(() => panelMode.value === 'modal')

function order(index: number) {
  return activePanels.value.length - 1 - index
}

function observeExtension(panel: PanelInstance, target: HTMLElement | null) {
  extensionObservers.get(panel.id)?.disconnect()
  extensionObservers.delete(panel.id)

  if (!target) {
    if (extensionActive.value[panel.id]) {
      extensionActive.value = { ...extensionActive.value, [panel.id]: false }
    }
    return
  }

  const update = () => {
    const active = target.childElementCount > 0
    if (extensionActive.value[panel.id] !== active) {
      extensionActive.value = { ...extensionActive.value, [panel.id]: active }
    }
  }

  const observer = new MutationObserver(update)
  observer.observe(target, { childList: true })
  extensionObservers.set(panel.id, observer)
  update()
}

function containerClass(index: number, panel: PanelInstance) {
  const cls = ['o-panel--container pointer-events-auto transition-all duration-200 flex']
  const hasExtension = extensionActive.value[panel.id] ?? false
  if (isModeModal.value) {
    cls.push('absolute inset-0 mx-auto h-95svh xl:h-90vh sm:max-h-2xl mt-auto sm:my-auto w-full pt-10 sm:pt-0 max-w-5xl')
  } else {
    cls.push('absolute right-0 top-0 my-0 h-full pt-10 md:p-4 md:pl-0 w-full')
    cls.push(hasExtension ? 'md:w-3/4 md:min-w-180 md:max-w-2xl' : 'md:w-1/2 md:min-w-180 md:max-w-210')
  }
  cls.push(order(index) ? 'o-panel-stacked' : 'o-panel-focused')
  return cls
}

function containerStyle(index: number) {
  const o = order(index)
  if (!o) return undefined
  return {
    '--transform': `-${50 * o}px`,
    '--scale': String(1 - 0.06 * o),
  } as Record<string, string>
}

function closeTop() {
  const t = store.top
  if (t) store.close(t)
}

function focus(index: number) {
  store.popTo(index)
}

function handlePopState(e: PopStateEvent) {
  store.onPopState(e)
}

onMounted(() => {
  window.addEventListener('popstate', handlePopState)
  // Restore the initial panel from URL hash (e.g. #customer/edit?id=123).
  if (route.hash) store.restoreFromHash(route.hash)
})

onBeforeUnmount(() => {
  window.removeEventListener('popstate', handlePopState)
  extensionObservers.forEach(observer => observer.disconnect())
})
</script>

<style lang="scss">
.o-panel {
  z-index: 70000;
  .o-panel--container {
    z-index: 700000;
  }
}
.o-panel--overlay {
  z-index: 60000;
}

/* Slide in/out for the full-screen panel wrapper */
.slide-panel-leave-active,
.slide-panel-enter-active {
  transition: 0.2s;
}

.slide-panel-enter-from {
  opacity: 0;
  transform: translate(0, 200px);
}

.slide-panel-leave-to {
  opacity: 0;
  transform: translate(0, 200px);
}

/* Stacked (backgrounded) panel offset + brightness dim */
.o-panel-stacked {
  transform: translateY(var(--transform)) scale(var(--scale));
  filter: brightness(0.8);
}

.o-panel-stacked:hover {
  bottom: 20px !important;
  filter: brightness(1) !important;
}

@media (min-width: 768px) {
  .right-panel .o-panel-stacked {
    transform: translateX(var(--transform)) scale(var(--scale));
  }

  .right-panel .o-panel-stacked:hover {
    bottom: 0px !important;
    right: 20px !important;
    transform: translateX(var(--transform)) scale(var(--scale));
  }

  .right-panel.slide-panel-enter-from {
    opacity: 0;
    transform: translate(200px, 0);
  }

  .right-panel.slide-panel-leave-to {
    opacity: 0;
    transform: translate(200px, 0);
  }
}
</style>
